import { isSectionVisible } from './clientSections'

/**
 * The handful of places worth one click from anywhere.
 *
 * The bar is not a second navigation — the sidebar is already that. A shortcut
 * earns its slot by being somewhere you go mid-task without wanting to lose
 * where you are, or by carrying a number that means something needs doing.
 *
 * Clients follow the same rule their sidebar follows: a room with nothing in it
 * is not offered. Being handed a shortcut to an empty page is worse than not
 * having the shortcut.
 */

export const SHORTCUTS = [
  // Start something, from wherever you are.
  { key: 'newProposal', label: 'New proposal', path: '/proposals?new=1', adminOnly: true },
  { key: 'newProject',  label: 'New project',  path: '/projects?new=1',  adminOnly: true },
  // Met someone: straight to the add form on Networking.
  { key: 'networking',  label: 'Add a contact (Networking)', path: '/networking?add=1', adminOnly: true },
  { key: 'upload',      label: 'Upload for review', path: '/document-reviews', adminOnly: true },
  { key: 'calendar',    label: 'Calendar',     path: '/schedule',        adminOnly: true },
  { key: 'approvals', label: 'Approvals', path: '/approvals', badge: 'approvals' },
  { key: 'invoices',  label: 'Invoices',  path: '/invoices',  badge: 'draftInvoices', adminOnly: true },
  { key: 'meetings',  label: 'Meetings',  path: '/meetings',  adminOnly: true },
  { key: 'files',     label: 'Files',     path: '/files',     clientOnly: true },
]

/**
 * Which shortcuts to draw.
 *
 * `counts` are the badge numbers. `sectionCounts` is what the client sidebar
 * uses to decide whether a section has anything behind it, and is ignored for
 * an admin, whose pages are never empty in the same way.
 */
export function visibleShortcuts({ isAdmin = false, counts = {}, sectionCounts, loaded = true } = {}) {
  return SHORTCUTS
    .filter(s => (isAdmin ? !s.clientOnly : !s.adminOnly))
    .filter(s => (isAdmin ? true : isSectionVisible(s.path, sectionCounts, { loaded })))
    .map(s => ({ ...s, count: badgeCount(s, counts) }))
}

function badgeCount(shortcut, counts) {
  if (!shortcut.badge) return 0
  const n = Number(counts?.[shortcut.badge])
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** Badges stop counting past nine; the exact number stops mattering there. */
export function badgeLabel(count) {
  if (!count || count < 1) return null
  return count > 9 ? '9+' : String(count)
}

/** What the tooltip says, so the icon never has to carry the meaning alone. */
export function shortcutTitle(shortcut) {
  if (!shortcut.count) return shortcut.label
  const noun = shortcut.key === 'approvals' ? 'awaiting you'
    : shortcut.key === 'invoices' ? 'not sent'
    : 'waiting'
  return `${shortcut.label} — ${shortcut.count} ${noun}`
}
